import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes';
import {
    BILLBOARD_DESKTOP_WIDTH,
    BILLBOARD_DESKTOP_HEIGHT,
    BILLBOARD_TABLET_WIDTH,
    BILLBOARD_TABLET_HEIGHT,
    BILLBOARD_MOBILE_WIDTH,
    BILLBOARD_MOBILE_HEIGHT,
    ELEMENT_ID_BILLBOARD,
    ELEMENT_ID_BILLBOARD_MOBILE,
    ELEMENT_ID_BILLBOARD_TABLET,
    ELEMENT_ID_PRODUCT_TILE_AD_PREFIX,
    PRODUCT_TILE_HEIGHT,
    PRODUCT_TILE_WIDTH,
    TILE_AD_KEY,
    HOME_TOP_BILLBOARD_PLACEMENT,
    HOME_BOTTOM_BILLBOARD_PLACEMENT,
    ORDER_CONFIRMATION_BILLBOARD_PLACEMENT,
    PDP_AVAILABLE_TOP_BILLBOARD_PLACEMENT,
    PDP_AVAILABLE_BOTTOM_BILLBOARD_PLACEMENT,
    PDP_CONDENSED_BOTTOM_BILLBOARD_PLACEMENT,
    PDP_CONDENSED_TOP_BILLBOARD_PLACEMENT,
    PDP_SOLD_BOTTOM_BILLBOARD_PLACEMENT,
    PDP_SOLD_TOP_BILLBOARD_PLACEMENT,
    BILLBOARD_TABLET_HEIGHT_V2,
    BILLBOARD_TABLET_WIDTH_V2,
    BILLBOARD_MOBILE_HEIGHT_V2,
    PRODUCT_TILE_WIDTH_V2,
    PRODUCT_TILE_HEIGHT_V2,
} from '../constants';
import { type AdData, type Ad } from '../types';

const createProductTileData = (
    productTileData?: Ad[],
    useV2Sizes?: boolean
): AdData['productTile'] => {
    let productTile: AdData['productTile'] = [];
    if (Array.isArray(productTileData)) {
        productTile = productTileData.map((tile, index) => {
            return {
                adId: tile.adId,
                elementId: `${ELEMENT_ID_PRODUCT_TILE_AD_PREFIX}-${index}`,
                width: useV2Sizes ? PRODUCT_TILE_WIDTH_V2 : PRODUCT_TILE_WIDTH,
                height: useV2Sizes ? PRODUCT_TILE_HEIGHT_V2 : PRODUCT_TILE_HEIGHT,
            };
        });
    }
    return productTile;
};

const createBillboardData = (
    billboardData?: {
        desktop?: Ad;
        mobile?: Ad;
        tablet?: Ad;
    },
    useV2Sizes?: boolean
): AdData['billboard'] => {
    const { desktop, tablet, mobile } = billboardData || {};

    const data: AdData['billboard'] = {};

    if (desktop) {
        data.desktop = {
            adId: desktop.adId,
            elementId: `${ELEMENT_ID_BILLBOARD}-${desktop.adId}`,
            width: BILLBOARD_DESKTOP_WIDTH,
            height: BILLBOARD_DESKTOP_HEIGHT,
        };
    }

    if (tablet) {
        data.tablet = {
            adId: tablet.adId,
            elementId: `${ELEMENT_ID_BILLBOARD_TABLET}-${tablet.adId}`,
            width: useV2Sizes ? BILLBOARD_TABLET_WIDTH_V2 : BILLBOARD_TABLET_WIDTH,
            height: useV2Sizes ? BILLBOARD_TABLET_HEIGHT_V2 : BILLBOARD_TABLET_HEIGHT,
        };
    }

    if (mobile) {
        data.mobile = {
            adId: mobile.adId,
            elementId: `${ELEMENT_ID_BILLBOARD_MOBILE}-${mobile.adId}`,
            width: BILLBOARD_MOBILE_WIDTH,
            height: useV2Sizes ? BILLBOARD_MOBILE_HEIGHT_V2 : BILLBOARD_MOBILE_HEIGHT,
        };
    }

    return data;
};

// only exporting for testing
export const generateAdData = ({
    productTileData,
    billboardData,
    useV2Sizes = false,
}: {
    productTileData?: Ad[];
    billboardData?: { desktop?: Ad; mobile?: Ad; tablet?: Ad };
    useV2Sizes?: boolean;
}): AdData => {
    return {
        productTile: createProductTileData(productTileData, useV2Sizes),
        billboard: createBillboardData(billboardData, useV2Sizes),
    };
};

const AD_JEWELRY = generateAdData({
    billboardData: {
        desktop: {
            adId: '/5391766/Jewelry_BottomBillboardBanner',
        },
        tablet: {
            adId: '/5391766/Small_Jewelry_BillboardBanner',
        },
        mobile: {
            adId: '/5391766/Jewelry_BillboardBanner_Mobile',
        },
    },
    productTileData: [
        { adId: '/5391766/FeaturedItem_Jewelry_ONE' },
        { adId: '/5391766/FeaturedItem_Jewelry_TWO' },
        { adId: '/5391766/FeaturedItem_Jewelry_THREE' },
        { adId: '/5391766/FeaturedItem_Jewelry_FOUR' },
    ],
});

const AD_FASHION = {
    ...generateAdData({
        productTileData: [
            { adId: '/5391766/FeaturedItem_Fashion_ONE' },
            { adId: '/5391766/FeaturedItem_Fashion_TWO' },
            { adId: '/5391766/FeaturedItem_Fashion_THREE' },
            { adId: '/5391766/FeaturedItem_Fashion_FOUR' },
        ],
        billboardData: {
            desktop: {
                adId: '/5391766/Fashion_BillboardBanner',
            },
            tablet: {
                adId: '/5391766/Small_Fashion_BillboardBanner',
            },
            mobile: {
                adId: '/5391766/Fashion_BillboardBanner_Mobile',
            },
        },
    }),
};

const AD_FURNITURE = generateAdData({
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner',
        },
        tablet: {
            adId: '/5391766/Small_Furniture_BillboardBanner',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_Mobile',
        },
    },
    productTileData: [
        { adId: '/5391766/Featured_Item_v2_ONE' },
        { adId: '/5391766/Featured_Item_v2_TWO' },
        { adId: '/5391766/Featured_Item_v2_THREE' },
        { adId: '/5391766/Featured_Item_v2_FOUR' },
    ],
});

const AD_ART = generateAdData({
    billboardData: {
        desktop: {
            adId: '/5391766/FineArt_BottomBillboard',
        },
        tablet: {
            adId: '/5391766/Small_FineArt_BillboardBanner',
        },
        mobile: {
            adId: '/5391766/FineArt_BillboardBanner_Mobile',
        },
    },
    productTileData: [
        { adId: '/5391766/FeaturedItem_FineArt_One' },
        { adId: '/5391766/FeaturedItem_FineArt_Two' },
    ],
});

const AD_SATURDAY_SALE = generateAdData({
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner_Sale',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_Sale',
        },
    },
    productTileData: [
        { adId: '/5391766/Featured_Item_Banner_Sale_Landing_1' },
        { adId: '/5391766/Featured_Item_Banner_Sale_Landing_2' },
        { adId: '/5391766/Featured_Item_Banner_Sale_Landing_3' },
        { adId: '/5391766/Featured_Item_Banner_Sale_Landing_4' },
    ],
});

const AD_DEFAULT = generateAdData({
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner',
        },
    },
});

const AD_CATALOGUE_COLLECTION = generateAdData({
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner_Catalogue',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_Catalogue_Mobile',
        },
    },
});

const AD_HOLIDAY_COLLECTION = generateAdData({
    productTileData: [
        { adId: '/5391766/Featured_Item_Banner_Holiday_Collection_2020_1' },
        { adId: '/5391766/Featured_Item_Banner_Holiday_Collection_2020_2' },
        { adId: '/5391766/Featured_Item_Banner_Holiday_Collection_2020_3' },
        { adId: '/5391766/Featured_Item_Banner_Holiday_Collection_2020_4' },
    ],
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner_Holiday_2020',
        },
        tablet: {
            adId: '/5391766/Bottom_Billboard_Banner_Holiday_2020_Tablet',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_Holiday_2020_Mobile',
        },
    },
});

const AD_HOLIDAY_SUB_COLLECTION = generateAdData({
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner_Holiday_2020_SubGiftGuides',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_Holiday_2020_SubGiftGuides_Mobile',
        },
    },
});

const AD_VALENTINES_COLLECTION = generateAdData({
    productTileData: [
        { adId: '/5391766/Featured_Item_Banner_Valentine_1' },
        { adId: '/5391766/Featured_Item_Banner_Valentine_2' },
        { adId: '/5391766/Featured_Item_Banner_Valentine_3' },
        { adId: '/5391766/Featured_Item_Banner_Valentine_4' },
    ],
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner_Valentine',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_Valentine_Mobile',
        },
    },
});

const AD_ENGAGEMENT_RINGS_COLLECTION = generateAdData({
    productTileData: [
        { adId: '/5391766/Featured_Item_Banner_Engagement_Rings_1' },
        { adId: '/5391766/Featured_Item_Banner_Engagement_Rings_2' },
        { adId: '/5391766/Featured_Item_Banner_Engagement_Rings_3' },
        { adId: '/5391766/Featured_Item_Banner_Engagement_Rings_4' },
    ],
});

const AD_MOTHERS_DAY_COLLECTION = generateAdData({
    productTileData: [
        { adId: '/5391766/Featured_Item_Banner_Mothers_Day_1' },
        { adId: '/5391766/Featured_Item_Banner_Mothers_Day_2' },
        { adId: '/5391766/Featured_Item_Banner_Mothers_Day_3' },
        { adId: '/5391766/Featured_Item_Banner_Mothers_Day_4' },
    ],
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner_Mothers_Day',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_Mothers_Day_Mobile',
        },
    },
});

const AD_FATHERS_DAY_COLLECTION = generateAdData({
    productTileData: [
        { adId: '/5391766/Featured_Item_Banner_Fathers_Day_1' },
        { adId: '/5391766/Featured_Item_Banner_Fathers_Day_2' },
        { adId: '/5391766/Featured_Item_Banner_Fathers_Day_3' },
        { adId: '/5391766/Featured_Item_Banner_Fathers_Day_4' },
    ],
});

const AD_SPRING_CATALOGUE = generateAdData({
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner_Spring_Catalogue',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_Spring_Catalogue_Mobile',
        },
    },
});

const AD_FALL_CATALOGUE = generateAdData({
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner_Fall_Catalogue',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_Fall_Catalogue_Mobile',
        },
    },
});

const AD_NEW_ARRIVALS = generateAdData({
    productTileData: [
        { adId: '/5391766/Featured_Item_Banner_New_Arrivals_1' },
        { adId: '/5391766/Featured_Item_Banner_New_Arrivals_2' },
        { adId: '/5391766/Featured_Item_Banner_New_Arrivals_3' },
        { adId: '/5391766/Featured_Item_Banner_New_Arrivals_4' },
    ],
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner_New_Arrivals',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_New_Arrivals_Mobile',
        },
    },
});

const AD_HOMEPAGE_TOP = generateAdData({
    billboardData: {
        desktop: {
            adId: '/5391766/home_top',
        },
        mobile: {
            adId: '/5391766/home_top',
        },
    },
    useV2Sizes: true,
});

const AD_HOMEPAGE_BOTTOM = generateAdData({
    billboardData: {
        tablet: {
            adId: '/5391766/home_bottom',
        },
        mobile: {
            adId: '/5391766/home_bottom',
        },
    },
    useV2Sizes: true,
});

const AD_CONFIRMATION = generateAdData({
    billboardData: {
        tablet: {
            adId: '/5391766/confirmation_page',
        },
        mobile: {
            adId: '/5391766/confirmation_page',
        },
    },
    useV2Sizes: true,
});

const AD_BUY_PAGE = generateAdData({
    productTileData: [
        { adId: '/5391766/buy_grid_1' },
        { adId: '/5391766/buy_grid_2' },
        { adId: '/5391766/buy_grid_3' },
        { adId: '/5391766/buy_grid_4' },
    ],
    billboardData: {
        tablet: {
            adId: '/5391766/buy_bottom',
        },
        mobile: {
            adId: '/5391766/buy_bottom',
        },
    },
    useV2Sizes: true,
});

const AD_PDP_AVAILABLE_TOP = generateAdData({
    billboardData: {
        tablet: {
            adId: '/5391766/pdp_available',
        },
        mobile: {
            adId: '/5391766/pdp_available',
        },
    },
    useV2Sizes: true,
});

const AD_PDP_AVAILABLE_BOTTOM = generateAdData({
    billboardData: {
        tablet: {
            adId: '/5391766/pdp_available_bottom',
        },
        mobile: {
            adId: '/5391766/pdp_available_bottom',
        },
    },
    useV2Sizes: true,
});

const AD_PDP_CONDENSED_TOP = generateAdData({
    billboardData: {
        tablet: {
            adId: '/5391766/pdp_condensed',
        },
        mobile: {
            adId: '/5391766/pdp_condensed',
        },
    },
    useV2Sizes: true,
});

const AD_PDP_CONDENSED_BOTTOM = generateAdData({
    billboardData: {
        tablet: {
            adId: '/5391766/pdp_condensed_bottom',
        },
        mobile: {
            adId: '/5391766/pdp_condensed_bottom',
        },
    },
    useV2Sizes: true,
});

const AD_PDP_SOLD_TOP = generateAdData({
    billboardData: {
        tablet: {
            adId: '/5391766/pdp_sold',
        },
        mobile: {
            adId: '/5391766/pdp_sold',
        },
    },
    useV2Sizes: true,
});

const AD_PDP_SOLD_BOTTOM = generateAdData({
    billboardData: {
        tablet: {
            adId: '/5391766/pdp_sold_bottom',
        },
        mobile: {
            adId: '/5391766/pdp_sold_bottom',
        },
    },
    useV2Sizes: true,
});

export const sbAdMap: { [n: string]: AdData } = {
    jewelry: AD_JEWELRY,
    fashion: AD_FASHION,
    furniture: AD_FURNITURE,
    art: AD_ART,
    contemporary: AD_FURNITURE,
    'saturday-sale': AD_SATURDAY_SALE,
    'design-gestalt': AD_CATALOGUE_COLLECTION,
    'holiday-gift-guide': AD_HOLIDAY_COLLECTION,
    'art-gifts': AD_HOLIDAY_SUB_COLLECTION,
    'jewelry-gifts': AD_HOLIDAY_SUB_COLLECTION,
    'rare-gifts': AD_HOLIDAY_SUB_COLLECTION,
    'iconic-gifts': AD_HOLIDAY_SUB_COLLECTION,
    'valentines-gift-guide': AD_VALENTINES_COLLECTION,
    'exquisite-engagement-rings': AD_ENGAGEMENT_RINGS_COLLECTION,
    'mothers-day-collection': AD_MOTHERS_DAY_COLLECTION,
    'fathers-day-collection': AD_FATHERS_DAY_COLLECTION,
    'spring-catalogue': AD_SPRING_CATALOGUE,
    'fall-catalogue': AD_FALL_CATALOGUE,
    'new-arrivals': AD_NEW_ARRIVALS,
    buy: AD_BUY_PAGE,
    // Below are not uriRef segments, but placement keys
    [HOME_TOP_BILLBOARD_PLACEMENT]: AD_HOMEPAGE_TOP,
    [HOME_BOTTOM_BILLBOARD_PLACEMENT]: AD_HOMEPAGE_BOTTOM,
    [ORDER_CONFIRMATION_BILLBOARD_PLACEMENT]: AD_CONFIRMATION,
    [PDP_AVAILABLE_TOP_BILLBOARD_PLACEMENT]: AD_PDP_AVAILABLE_TOP,
    [PDP_AVAILABLE_BOTTOM_BILLBOARD_PLACEMENT]: AD_PDP_AVAILABLE_BOTTOM,
    [PDP_CONDENSED_TOP_BILLBOARD_PLACEMENT]: AD_PDP_CONDENSED_TOP,
    [PDP_CONDENSED_BOTTOM_BILLBOARD_PLACEMENT]: AD_PDP_CONDENSED_BOTTOM,
    [PDP_SOLD_TOP_BILLBOARD_PLACEMENT]: AD_PDP_SOLD_TOP,
    [PDP_SOLD_BOTTOM_BILLBOARD_PLACEMENT]: AD_PDP_SOLD_BOTTOM,
    default: AD_DEFAULT,
} as const;

export const sbAdExcludeMap: Record<string, readonly string[]> = {
    [PAGE_TYPE.MORE_FROM_SELLER]: [TILE_AD_KEY],
    [PAGE_TYPE.DEALERS]: [TILE_AD_KEY],
} as const;
